import { useState } from "react";
import { AlertColor, Box, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import alertIcon from "../../assets/images/alert_icon.png";
import editIcon from "../../assets/images/edit_icon.png";
import viewIcon from "../../assets/images/view_icon.png";

import BoxContent from "../../components/boxContent";
import DataGridApi from "../../components/dataGridApi";
import ModalAddCampaign from "./modalAddCampaign";
import ModalEditCampaign from "./modalEditCampaign";
import ModalViewCampaign from "./modalViewCampaign";
import ModalViewErrors from "./modalViewErrors";
import QuickAlert from "../../components/form/quickAlert";
import ModalOttoReport from "./modalOttoReport";

export default function OttoTrialPeriod() {
  const [dataUpdated, setDataUpdated] = useState(0);
  const [openModalAddCampaign, setOpenModalAddCampaign] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState<any>(null);
  const [campaignToView, setCampaignToView] = useState<any>(null);
  const [campaignToViewErrors, setCampaignToViewErrors] = useState<any>(null);
  const [openModalReport, setOpenModalReport] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState<boolean>(false);
  const [severityAlert, setSeverityAlert] = useState<AlertColor>("success");
  const [messageQuickAlert, setMessageQuickAlert] = useState("");
  const [titleMessage, setTitleMessage] = useState("");
  const [actionType, setActionType] = useState("");

  const { t } = useTranslation();

  const formatStatus = (status?: string) => {
    if (!status) return "-";

    const translated = t(`ottoTrialPeriod.status.${status}`);
    return translated === `ottoTrialPeriod.status.${status}`
      ? status
      : translated;
  };

  const refreshPage = () => {
    setDataUpdated((prev) => prev + 1);
  };

  const openSucessAlert = (message?: string) => {
    setVisibleAlert(true);
    setSeverityAlert("success");
    setMessageQuickAlert(message || t("ottoTrialPeriod.alert.successMessage"));
    setTitleMessage(t("quickAlert.sucess"));
  };

  const openErrorAlert = (errorMessage?: string) => {
    setVisibleAlert(true);
    setSeverityAlert("error");
    setMessageQuickAlert(
      errorMessage ?? t("ottoTrialPeriod.alert.errorMessage"),
    );
    setTitleMessage(t("quickAlert.error"));
  };

  const setActionTypeHandler = (action: string) => {
    setActionType(action);
  };

  const formatParticipants = (rules?: any[], participantCount?: number) => {
    if (Array.isArray(rules) && rules.length === 1 && rules[0]?.email) {
      return rules[0].email;
    }

    if (participantCount !== undefined && participantCount !== null) {
      const participantLabel =
        participantCount === 1
          ? t("ottoTrialPeriod.list.participantSingle")
          : t("ottoTrialPeriod.list.participantPlural");

      return `${participantCount} ${participantLabel}`;
    }

    return "-";
  };

  const STATUS_LABEL_MAP: Record<string, string> = {
    ativa: "ACTIVE",
    encerrada: "INACTIVE",
    desativada: "CANCELLED",

    active: "ACTIVE",
    closed: "INACTIVE",
    disabled: "CANCELLED",

    activa: "ACTIVE",
    cerrada: "INACTIVE",
    desactivada: "CANCELLED",
  };

  const statusSearchTransformer = (value: string): string => {
    const normalized = value.trim().toLowerCase();
    return STATUS_LABEL_MAP[normalized] ?? value;
  };

  const ottoColumns = [
    {
      field: "name",
      headerName: t("ottoTrialPeriod.columns.campaign"),
      flex: 1,
      minWidth: 180,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) => value || "-",
    },
    {
      field: "startDate",
      headerName: t("ottoTrialPeriod.columns.startDate"),
      width: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      field: "endDate",
      headerName: t("ottoTrialPeriod.columns.endDate"),
      width: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      field: "modelName",
      headerName: t("ottoTrialPeriod.columns.model"),
      width: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) => value ?? "-",
    },
    {
      field: "versionName",
      headerName: t("ottoTrialPeriod.columns.version"),
      width: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) => value ?? "-",
    },
    {
      field: "vehicleYear",
      headerName: t("ottoTrialPeriod.columns.year"),
      width: 80,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) => value ?? "-",
    },
    {
      field: "status",
      headerName: t("ottoTrialPeriod.columns.status"),
      width: 110,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: any) => formatStatus(value),
    },
    {
      field: "rules",
      headerName: t("ottoTrialPeriod.columns.participants"),
      width: 120,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const participants = formatParticipants(
          params?.row?.rules,
          params?.row?.participantCount,
        );

        return (
          <span
            title={participants}
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {participants}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: t("ottoTrialPeriod.columns.actions"),
      width: 130,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setCampaignToEdit(params.row);
            }}
          >
            <img
              src={editIcon}
              alt={t("ottoTrialPeriod.actions.edit")}
              width={22}
              height={20}
            />
          </IconButton>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setCampaignToView(params.row);
            }}
          >
            <img
              src={viewIcon}
              alt={t("ottoTrialPeriod.actions.view")}
              width={22}
              height={14}
            />
          </IconButton>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setCampaignToViewErrors(params.row);
            }}
          >
            <img
              src={alertIcon}
              alt={t("ottoTrialPeriod.actions.notify")}
              width={21}
              height={19}
            />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <BoxContent
      title={t("ottoTrialPeriod.list.title")}
      button
      titleButton={
        window.innerWidth > 900
          ? t("ottoTrialPeriod.list.textRelatoryButton")
          : t("ottoTrialPeriod.list.textRelatoryButton2")
      }
      handleClick={() => setOpenModalReport(true)}
      button2
      titleButton2={t("ottoTrialPeriod.list.addButtonText")}
      handleClick2={() => setOpenModalAddCampaign(true)}
    >
      <Box
        sx={{
          marginBottom: 3,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 400 }}>
          {t("ottoTrialPeriod.list.description")}
        </span>
      </Box>

      <Box
        sx={{
          marginBottom: 2,
        }}
      >
        <span style={{ fontSize: 12 }}>
          {t("ottoTrialPeriod.list.searchDescription")}
        </span>
      </Box>

      <DataGridApi
        onRowClick={(params: any) => {
          // handleOnRowClick(params.row);
        }}
        mock={[]}
        columnsData={ottoColumns}
        endPoint="temporaryAccess?"
        labelParams1={t("ottoTrialPeriod.list.searchInputText")}
        globalSearchEnabled
        globalSearchParam="&searchText="
        filterInputWidth={270}
        cleanButtonText={t("ottoTrialPeriod.list.cleanButtonText")}
        cleanButtonType="outlined"
        dataUpdated={dataUpdated}
        onEmptyMessageApi={t("ottoTrialPeriod.list.emptyMessage")}
        searchTextTransformer={statusSearchTransformer}
      />

      <QuickAlert
        visibility={visibleAlert}
        description={messageQuickAlert}
        severity={severityAlert}
        title={titleMessage}
        hanhleClose={() => setVisibleAlert(false)}
      />

      {openModalAddCampaign && (
        <ModalAddCampaign
          onClose={() => setOpenModalAddCampaign(false)}
          openSucessAlert={openSucessAlert}
          openErrorAlert={openErrorAlert}
          refreshPage={refreshPage}
          setActionType={setActionTypeHandler}
        />
      )}

      {campaignToEdit && (
        <ModalEditCampaign
          campaign={campaignToEdit}
          onClose={() => setCampaignToEdit(null)}
          openSucessAlert={openSucessAlert}
          openErrorAlert={openErrorAlert}
          refreshPage={refreshPage}
        />
      )}

      {campaignToView && (
        <ModalViewCampaign
          campaign={campaignToView}
          onClose={() => setCampaignToView(null)}
        />
      )}

      {campaignToViewErrors && (
        <ModalViewErrors
          campaign={campaignToViewErrors}
          onClose={() => setCampaignToViewErrors(null)}
        />
      )}

      {openModalReport && (
        <ModalOttoReport
          onClose={() => setOpenModalReport(false)}
          openSucessAlert={openSucessAlert}
          openErrorAlert={openErrorAlert}
        />
      )}
    </BoxContent>
  );
}
